import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, RouteReuseStrategy } from '@angular/router';

import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry } from '@ag-grid-community/core';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalService,
  ProtectedResourceScopes,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { TranslateModule } from '@ngx-translate/core';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { StarRatingConfigService, StarRatingModule } from 'angular-star-rating';
import {
  Configuration,
  ConfigurationParameters,
  XatApiModule,
} from 'net-api-lib';
import { pendingRequestsInterceptor$ } from 'ng-http-loader';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideToastr } from 'ngx-toastr';
import { catchError, EMPTY, switchMap, take } from 'rxjs';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routing';
import { loginRequest, msalConfig } from './core/auth/auth-config';
import { AuthService, AZURE_AUTH_API_ENDPOINT } from './core/auth/auth.service';
import { AzureB2CAuthInterceptor } from './core/auth/azure-b2c-auth.interceptor';
import { CoreModule } from './core/core.module';
import { EuropeanDateAdapter } from './core/european-date-adapter';
import { FlagBasedReuseStrategy } from './core/flag-based-reuse-strategy';
import { httpErrorInterceptor } from './core/http-error.interceptor';
import { CustomStarRatingService } from './shared/custom-star-rating.service';
import { FormlyExtensionModule } from './shared/formly/formly-extension/formly-extension.module';

export function storageFactory(): OAuthStorage {
  return sessionStorage;
}

// Register AG-Grid Modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  InfiniteRowModelModule,
  CsvExportModule,
]);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes),
    { provide: RouteReuseStrategy, useClass: FlagBasedReuseStrategy },
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      TranslateModule.forRoot(),
      OAuthModule.forRoot(),
      StarRatingModule.forRoot(),

      CoreModule,

      XatApiModule.forRoot(xatenaApiConfigFactory),

      // TODO: Formly modules, remove later
      // FormlyMatIntlTelInputModule,
      FormlyMatDatepickerModule,
      FormlyExtensionModule,
      MatNativeDateModule
    ),
    provideAnimations(), // required animations providers
    provideToastr({
      maxOpened: 5,
      timeOut: 10000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: false,
      progressBar: true,
      newestOnTop: false,
    }), // Toastr providers

    provideCharts(withDefaultRegisterables()),

    provideHttpClient(
      withInterceptorsFromDi(),
      withFetch(),
      withInterceptors([httpErrorInterceptor, pendingRequestsInterceptor$])
    ),
    provideLoadingBarInterceptor(),
    { provide: OAuthStorage, useFactory: storageFactory },

    // Refresh Xatena-Token on app start
    provideAppInitializer(() => {
      const authService = inject(AuthService);

      return authService.isAuthenticated().pipe(
        take(1),
        switchMap(isAuthenticated => {
          if (isAuthenticated) return authService.refreshAccessToken();
          return EMPTY;
        }),
        catchError(() => {
          console.error(
            'appInitializer: Failed to refresh access token, logging out'
          );

          authService.signOut(); // sign out if refresh fails
          return EMPTY;
        })
      );
    }),

    //MSAL
    {
      provide: AZURE_AUTH_API_ENDPOINT,
      useFactory: azureAuthApiEndpointFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AzureB2CAuthInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: StarRatingConfigService,
      useClass: CustomStarRatingService,
    },

    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // Override the NativeDateAdapter so that manually typed European dates
    // (dd.MM.yyyy) are parsed correctly instead of being swapped to MM.DD.YYYY.
    { provide: DateAdapter, useClass: EuropeanDateAdapter },
  ],
};

export function xatenaApiConfigFactory(): Configuration {
  if (
    !environment.netApiUrl.startsWith('http://') &&
    !environment.netApiUrl.startsWith('https://')
  ) {
    if (!environment.netApiUrl.startsWith('/')) {
      environment.netApiUrl = `/${environment.netApiUrl}`;
    }
    environment.netApiUrl = `${location.protocol}//${location.host}${environment.netApiUrl}`;
  }
  if (environment.netApiUrl.endsWith('/')) {
    environment.netApiUrl = environment.netApiUrl.substring(
      0,
      environment.netApiUrl.length - 1
    );
  }

  const params: ConfigurationParameters = {
    basePath: environment.netApiUrl,
    withCredentials: false,
  };

  return new Configuration(params);
}

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: msalConfig.auth,
    cache: msalConfig.cache,
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii) => {
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              console.debug(message);
              return;
          }
        },
      },
    },
  });
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const azureAuthApiEndpoint = azureAuthApiEndpointFactory();

  const protectedResourceMap = new Map<
    string,
    (string | ProtectedResourceScopes)[] | null
  >();

  protectedResourceMap.set(azureAuthApiEndpoint, [
    {
      httpMethod: 'GET',
      scopes: [...environment.msalConfig.protectedResources.api.scopes.read],
    },
    {
      httpMethod: 'POST',
      scopes: [...environment.msalConfig.protectedResources.api.scopes.read],
    },
  ]);

  //console.log('protectedResourceMap', protectedResourceMap);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function azureAuthApiEndpointFactory(): string {
  if (
    !environment.azureAuthApi.endpoint.startsWith('http://') &&
    !environment.azureAuthApi.endpoint.startsWith('https://')
  ) {
    if (!environment.azureAuthApi.endpoint.startsWith('/')) {
      environment.azureAuthApi.endpoint = `/${environment.azureAuthApi.endpoint}`;
    }

    environment.azureAuthApi.endpoint = `${location.protocol}//${location.host}${environment.azureAuthApi.endpoint}`;
  }

  return environment.azureAuthApi.endpoint;
}
